import { Dispatch, ReactNode, createContext, useContext, useId, useReducer } from 'react'

export type UpsellModalState = {
  ariaDescribedById: string | undefined
  ariaLabelledById: string | undefined
  currentStep: number
  heapId: string | undefined
}
const DEFAULT_CURRENT_STEP = 1
const initialUpsellModalState: UpsellModalState = {
  ariaDescribedById: undefined,
  ariaLabelledById: undefined,
  currentStep: DEFAULT_CURRENT_STEP,
  heapId: undefined
}

const UpsellModalContext = createContext<UpsellModalState | undefined>(undefined)
const UpsellModalDispatchContext = createContext<React.Dispatch<UpsellReducerAction> | undefined>(
  undefined
)

export type UpsellReducerAction =
  | { type: 'INCREMENT_STEP' }
  | { type: 'DECREMENT_STEP' }
  | { type: 'RESET_STEP' }

/**
 * Reducer for the UpsellModalContextProvider
 * Reducer pattern is probably overkill for this use case but
 * if the state grows over time this is a great pattern to use
 */
export function upsellModalReducer(
  state: UpsellModalState,
  action: UpsellReducerAction
): UpsellModalState {
  switch (action.type) {
    case 'DECREMENT_STEP':
      return { ...state, currentStep: state.currentStep - 1 }
    case 'INCREMENT_STEP':
      return { ...state, currentStep: state.currentStep + 1 }
    case 'RESET_STEP':
      return { ...state, currentStep: DEFAULT_CURRENT_STEP }
    default:
      return state
  }
}

export const useUpsellModalContext = (): UpsellModalState => {
  const context = useContext(UpsellModalContext)

  if (!context) {
    throw new Error('useUpsellModalContext must be used within the UpsellModalContextProvider')
  }

  return context
}

/**
 * This hook returns the dispatch function for the UpsellModalContextProvider
 * @returns React.Dispatch<UpsellReducerAction>
 */
export const useUpsellModalDispatch = (): Dispatch<UpsellReducerAction> => {
  const context = useContext(UpsellModalDispatchContext)

  if (!context) {
    throw new Error('useUpsellModalDispatch must be used within the UpsellModalContextProvider')
  }

  return context
}

/**
 * Context Provider for the UpsellModalContext
 * Could be extended to take in initial state and merge with initialUpsellModalState
 * The dispatch function is split out in a separate context to avoid unnecessary re-renders
 * i.e. nested children may only need to dispatch an action and not consume the state
 */
export function UpsellModalContextProvider({
  children,
  value
}: {
  children: ReactNode
  value?: Partial<UpsellModalState>
}): JSX.Element {
  const ariaLabelledById = useId()
  const ariaDescribedById = useId()
  const [state, dispatch] = useReducer(upsellModalReducer, {
    ...initialUpsellModalState,
    ...value,
    ariaDescribedById,
    ariaLabelledById
  })

  return (
    <UpsellModalContext.Provider value={state}>
      <UpsellModalDispatchContext.Provider value={dispatch}>
        {children}
      </UpsellModalDispatchContext.Provider>
    </UpsellModalContext.Provider>
  )
}
